import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  GET_ALL_CART_PRODUCT,
} from '../../../Constants/Config';
import {
  applyPriceBookToItems,
  CHECKOUT_PRICE_MAX_AGE_MS,
} from '../../../services/priceBook/priceBookStore';

const LEGACY_CART_PRODUCTS_CACHE_KEY = 'cached_cart_products';
const LEGACY_MERCHANT_CART_PRODUCTS_CACHE_PREFIX = 'cached_cart_products_';
const CART_PRODUCTS_CACHE_PREFIX = 'cached_cart_products:';
const cartProductDetailRequests = new Map();

const normalizeMerchantId = (merchantId) =>
  String(merchantId ?? '').trim();

const getCartProductIds = (data) =>
  Array.from(
    new Set(
      String(data?.product_id ?? '')
        .split(',')
        .map((productId) => productId.trim())
        .filter(Boolean),
    ),
  ).sort();

const getCartProductRequestKey = (data) => {
  const merchantId = normalizeMerchantId(data?.merchant_id).toUpperCase();

  return `${merchantId}|${getCartProductIds(data).join(',')}`;
};

const getCartProductCacheKey = (requestKey) =>
  `${CART_PRODUCTS_CACHE_PREFIX}${requestKey}`;

const getMerchantCartProductCacheKey = (merchantId) => {
  const normalizedMerchantId = normalizeMerchantId(merchantId);
  return normalizedMerchantId
    ? `${LEGACY_MERCHANT_CART_PRODUCTS_CACHE_PREFIX}${normalizedMerchantId}`
    : null;
};

const readProductCache = (cacheKey) => {
  if (!cacheKey || typeof localStorage === 'undefined') return null;

  try {
    const serializedProducts = localStorage.getItem(cacheKey);
    if (serializedProducts === null) return null;

    const cachedProducts = JSON.parse(serializedProducts);
    if (Array.isArray(cachedProducts)) return cachedProducts;

    localStorage.removeItem(cacheKey);
    return null;
  } catch {
    return null;
  }
};

const readCachedCartProducts = (requestKey, data) => {
  const requestCache = readProductCache(getCartProductCacheKey(requestKey));
  if (requestCache !== null) return requestCache;

  const merchantCache = readProductCache(
    getMerchantCartProductCacheKey(data?.merchant_id),
  );
  if (merchantCache === null) return null;

  const requestedProductIds = new Set(getCartProductIds(data));
  return merchantCache.filter((product) =>
    requestedProductIds.has(String(product?.id ?? '').trim()),
  );
};

const writeCachedCartProducts = (requestKey, merchantId, products) => {
  if (!Array.isArray(products) || typeof localStorage === 'undefined') return;

  const cacheKeys = [
    requestKey ? getCartProductCacheKey(requestKey) : null,
    getMerchantCartProductCacheKey(merchantId),
  ].filter(Boolean);

  cacheKeys.forEach((cacheKey) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(products));
    } catch {
      // Cart hydration can continue even when browser storage is unavailable.
    }
  });
};

const removeCartProductCache = (cacheKey) => {
  if (!cacheKey || typeof localStorage === 'undefined') return;

  try {
    localStorage.removeItem(cacheKey);
  } catch {
    // State still needs to be cleared when browser storage is unavailable.
  }
};

const isResetCartRequest = (data) =>
  !data ||
  (Array.isArray(data) && data.length === 0) ||
  (typeof data === 'object' && Object.keys(data).length === 0);

const isBrowserOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

const initialState = {
  loading: false,
  cartProductDetails: [],
  error: '',
  lastUpdated: null,
  isOffline: false,
  currentRequestKey: null,
  merchantId: null,
  currentRequestId: null,
  // Single product validation state
  singleProductLoading: false,
  singleProductData: null,
  singleProductError: ''
};

/**
 * Fetches single cart product details for validation (used in ProductCard)
 * This is used to check stock availability before adding to cart
 */
export const fetchSingleCartProduct = createAsyncThunk(
  'cartProducts/fetchSingleCartProduct',
  async ({ merchant_id, product_id }, { rejectWithValue }) => {
    // Check for network connectivity
    if (!navigator.onLine) {
      return rejectWithValue({ isOffline: true, message: 'No internet connection' });
    }

    // Validate input data
    if (!merchant_id || !product_id) {
      return rejectWithValue({ message: 'Missing merchant_id or product_id' });
    }

    try {
      const response = await axios({
        method: 'post',
        url: `${BASE_URL_SANDBOX}${GET_ALL_CART_PRODUCT}`,
        data: {
          merchant_id: merchant_id,
          product_id: product_id,
        },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10000, // 10 second timeout
        validateStatus: (status) => status >= 200 && status < 300
      });

      // Return the first product from the response
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }
      
      return null;
    } catch (error) {
      
      return rejectWithValue({
        message: error.message || 'Failed to fetch product details',
        isOffline: !navigator.onLine,
        code: error.code
      });
    }
  }
);

/**
 * Fetches cart product details from the server
 * Falls back to cached data when offline or on network error
 */
export const fetchAllCartProductDetails = createAsyncThunk(
  'cartProducts/fetchAllCartProductDetails',
  async (data, { rejectWithValue }) => {
    const merchantId = normalizeMerchantId(data?.merchant_id);

    if (isResetCartRequest(data)) {
      return { merchantId: null, products: [], isOffline: false };
    }

    const productIds = getCartProductIds(data);
    if (!merchantId || productIds.length === 0) {
      return rejectWithValue({
        merchantId,
        message: 'Missing merchant_id or product_id',
      });
    }

    const requestKey = getCartProductRequestKey(data);
    const cachedProducts = readCachedCartProducts(requestKey, data);

    if (isBrowserOffline()) {
      if (cachedProducts !== null) {
        return { merchantId, products: cachedProducts, isOffline: true };
      }

      return rejectWithValue({
        merchantId,
        isOffline: true,
        message: 'No internet connection',
      });
    }

    try {
      let request = cartProductDetailRequests.get(requestKey);

      if (!request) {
        request = axios({
          method: 'post',
          url: `${BASE_URL_SANDBOX}${GET_ALL_CART_PRODUCT}`,
          data,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 10000,
          validateStatus: (status) => status >= 200 && status < 300,
        }).then(async (response) => {
          const products = await applyPriceBookToItems(
            merchantId,
            Array.isArray(response?.data) ? response.data : [],
            { maxAgeMs: CHECKOUT_PRICE_MAX_AGE_MS },
          );
          writeCachedCartProducts(requestKey, merchantId, products);
          return products;
        });
        cartProductDetailRequests.set(requestKey, request);
      }

      try {
        const products = await request;
        return { merchantId, products, isOffline: false };
      } finally {
        if (cartProductDetailRequests.get(requestKey) === request) {
          cartProductDetailRequests.delete(requestKey);
        }
      }
    } catch (error) {
      if (cachedProducts !== null) {
        return {
          merchantId,
          products: cachedProducts,
          isOffline: true,
        };
      }

      return rejectWithValue({
        merchantId,
        message: error?.message || 'Failed to fetch cart products',
        isOffline: isBrowserOffline(),
        code: error?.code,
      });
    }
  },
);
export const fetchAllCartProductDetailsOld = createAsyncThunk(
  'cartProducts/fetchAllCartProductDetails',
  async (data, { rejectWithValue }) => {
    const merchantId = normalizeMerchantId(data?.merchant_id);

    try {
      if (data) {
        const response = await axios.post(
          BASE_URL_SANDBOX + GET_ALL_CART_PRODUCT,
          data,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 10000,
          },
        );

        if (response.status === 200) {
          return {
            merchantId,
            products: Array.isArray(response.data) ? response.data : [],
            isOffline: false,
          };
        }
      } else {
        return { merchantId: null, products: [], isOffline: false };
      }
    } catch (error) {
      return rejectWithValue({
        merchantId,
        message: error?.message || 'Failed to fetch cart products',
        isOffline: !navigator.onLine,
      });
    }

    return { merchantId, products: [], isOffline: false };
  },
);

const cartProductDetailsSlice = createSlice({
  name: 'cartProducts',
  initialState,
  reducers: {
    // Action to manually update cart products (useful for offline updates)
    updateCachedCartProducts: (state, action) => {
      const products = Array.isArray(action.payload?.products)
        ? action.payload.products
        : Array.isArray(action.payload)
          ? action.payload
          : [];
      const merchantId = normalizeMerchantId(
        action.payload?.merchantId ??
          action.payload?.merchant_id ??
          state.merchantId,
      );

      state.cartProductDetails = products;
      state.merchantId = merchantId || null;
      state.lastUpdated = new Date().toISOString();
      writeCachedCartProducts(
        state.currentRequestKey,
        merchantId,
        state.cartProductDetails,
      );
    },
    // Action to clear cart products cache
    clearCartProductsCache: (state) => {
      if (state.currentRequestKey) {
        removeCartProductCache(getCartProductCacheKey(state.currentRequestKey));
      }
      removeCartProductCache(getMerchantCartProductCacheKey(state.merchantId));
      removeCartProductCache(LEGACY_CART_PRODUCTS_CACHE_KEY);
      state.cartProductDetails = [];
      state.loading = false;
      state.error = '';
      state.isOffline = false;
      state.lastUpdated = null;
      state.currentRequestKey = null;
      state.merchantId = null;
      state.currentRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Single product fetch reducers
      .addCase(fetchSingleCartProduct.pending, (state) => {
        state.singleProductLoading = true;
        state.singleProductError = '';
        state.singleProductData = null;
      })
      .addCase(fetchSingleCartProduct.fulfilled, (state, action) => {
        state.singleProductLoading = false;
        state.singleProductData = action.payload;
        state.singleProductError = '';
      })
      .addCase(fetchSingleCartProduct.rejected, (state, action) => {
        state.singleProductLoading = false;
        state.singleProductData = null;
        state.singleProductError = action.payload?.message || 'Failed to fetch product details';
      })
      // All cart products fetch reducers
      .addCase(fetchAllCartProductDetails.pending, (state, action) => {
        const requestKey = isResetCartRequest(action.meta.arg)
          ? null
          : getCartProductRequestKey(action.meta.arg);
        if (state.currentRequestKey !== requestKey) {
          state.cartProductDetails = [];
        }
        state.currentRequestKey = requestKey;
        state.loading = true;
        state.error = '';
        state.isOffline = false;
        state.merchantId =
          normalizeMerchantId(action.meta.arg?.merchant_id) || null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchAllCartProductDetails.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.cartProductDetails = Array.isArray(action.payload)
          ? action.payload
          : Array.isArray(action.payload?.products)
            ? action.payload.products
            : [];
        state.error = '';
        state.isOffline = Boolean(action.payload?.isOffline);
        state.merchantId =
          normalizeMerchantId(
            action.payload?.merchantId ?? action.meta.arg?.merchant_id,
          ) || null;
        state.currentRequestId = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchAllCartProductDetails.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.isOffline = action.payload?.isOffline || isBrowserOffline();
        state.cartProductDetails = [];
        state.merchantId =
          normalizeMerchantId(
            action.payload?.merchantId ?? action.meta.arg?.merchant_id,
          ) || null;
        state.currentRequestId = null;
        state.error =
          action.payload?.message || 'Failed to fetch cart products';
      });
  },
});

export const { updateCachedCartProducts, clearCartProductsCache } = cartProductDetailsSlice.actions;

export default cartProductDetailsSlice.reducer;
